import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { getAdminActivity, revokeAdminSession } from "../../../../api/AdminApi";

const formatDate = (value) => value ? new Date(String(value).replace(" ", "T")).toLocaleString("es-PY") : "—";
const deviceName = (agent = "") => {
  if (/Android/i.test(agent)) return "Android";
  if (/iPhone|iPad/i.test(agent)) return "iPhone/iPad";
  if (/Windows/i.test(agent)) return "Windows";
  if (/Macintosh/i.test(agent)) return "Mac";
  if (/Linux/i.test(agent)) return "Linux";
  return agent ? "Otro dispositivo" : "—";
};
const detailText = (details) => {
  if (!details) return "—";
  try { return JSON.stringify(typeof details === "string" ? JSON.parse(details) : details).slice(0, 180); }
  catch (_) { return String(details).slice(0, 180); }
};
const eventLabel = (type) => ({
  LOGIN: "Inicio de sesión",
  LOGOUT: "Cierre de sesión",
  CLICK: "Clic",
  DOWNLOAD: "Descarga",
  CHANGE: "Cambio",
  NAVIGATION: "Navegación",
  ACTION: "Acción",
}[type] || type || "Acción");
const actionLabel = (action = "") => {
  const match = String(action).match(/^(GET|POST|PUT|PATCH|DELETE)\s+(.+)$/i);
  if (!match) return action || "—";
  const labels = { GET: "Consulta", POST: "Creación / acción", PUT: "Modificación", PATCH: "Modificación", DELETE: "Eliminación" };
  return `${labels[match[1].toUpperCase()] || match[1]}: ${match[2]}`;
};

export default function ModalAdminActivity({ show, admin, onHide, onChanged }) {
  const [data, setData] = useState({ sessions: [], activity: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!admin?.idadmin) return;
    setLoading(true);
    try { setData(await getAdminActivity(admin.idadmin)); }
    catch (error) { Swal.fire("Error", error.message, "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (show) load(); }, [show, admin?.idadmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (session) => {
    const result = await Swal.fire({
      title: "¿Cerrar esta sesión?", text: "El dispositivo perderá acceso en su próxima solicitud.", icon: "warning",
      showCancelButton: true, confirmButtonText: "Sí, cerrar", cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;
    try {
      await revokeAdminSession(session.session_id);
      await load();
      onChanged?.();
      Swal.fire({ icon: "success", title: "Sesión cerrada", timer: 1300, showConfirmButton: false });
    } catch (error) { Swal.fire("Error", error.message, "error"); }
  };

  const exportActivity = () => {
    const rows = data.activity.map((item) => ({
      "Fecha y hora": formatDate(item.created_at),
      Tipo: eventLabel(item.event_type),
      "Acción": actionLabel(item.action),
      Ruta: item.route || "",
      IP: item.ip_address || "",
      Detalles: detailText(item.details),
      "ID de sesión": item.session_id || "",
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet["!cols"] = [{ wch: 21 }, { wch: 20 }, { wch: 42 }, { wch: 45 }, { wch: 18 }, { wch: 60 }, { wch: 34 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Actividad");
    const account = String(admin?.cuenta || "administrador").replace(/[^a-zA-Z0-9_-]/g, "_");
    XLSX.writeFile(book, `Actividad_${account}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton><Modal.Title>Actividad de {admin?.cuenta}</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading ? <div className="text-center py-5"><Spinner animation="border" /></div> : (
          <Tabs defaultActiveKey="sessions">
            <Tab eventKey="sessions" title={`Sesiones (${data.sessions.length})`}>
              <Table responsive size="sm" className="mt-3 align-middle">
                <thead><tr><th>Estado</th><th>Ingreso</th><th>Última actividad</th><th>Origen</th><th>Dispositivo</th><th></th></tr></thead>
                <tbody>{data.sessions.map((session) => (
                  <tr key={session.session_id}>
                    <td><Badge bg={session.is_active ? "success" : session.revoked_at ? "danger" : "secondary"}>{session.is_active ? "EN LÍNEA" : session.revoked_at ? "REVOCADA" : "CERRADA"}</Badge></td>
                    <td>{formatDate(session.logged_in_at)}</td>
                    <td>{formatDate(session.last_activity_at)}</td>
                    <td><div>{session.ip_address || "—"}</div><small className="text-muted">{session.location_label || session.timezone || "Ubicación no informada"}</small></td>
                    <td title={session.user_agent || ""}>{deviceName(session.user_agent)}</td>
                    <td>{session.is_active ? <Button size="sm" variant="outline-danger" onClick={() => revoke(session)}>Quitar</Button> : null}</td>
                  </tr>
                ))}</tbody>
              </Table>
              {!data.sessions.length && <Alert variant="info" className="mt-3">Esta cuenta todavía no tiene sesiones registradas.</Alert>}
            </Tab>
            <Tab eventKey="activity" title={`Actividad (${data.activity.length})`}>
              <div className="d-flex justify-content-end mt-3">
                <Button variant="outline-success" size="sm" onClick={exportActivity} disabled={!data.activity.length}>
                  <i className="fas fa-file-excel me-1"></i> Descargar Excel
                </Button>
              </div>
              <Table responsive size="sm" className="mt-3 align-middle">
                <thead><tr><th>Fecha y hora</th><th>Tipo</th><th>Acción</th><th>Ruta</th><th>IP</th><th>Resultado / datos</th></tr></thead>
                <tbody>{data.activity.map((item) => (
                  <tr key={item.id}>
                    <td className="text-nowrap">{formatDate(item.created_at)}</td>
                    <td><Badge bg={item.event_type === "DOWNLOAD" ? "info" : item.event_type === "CHANGE" ? "warning" : "secondary"}>{eventLabel(item.event_type)}</Badge></td>
                    <td>{actionLabel(item.action)}</td><td><small>{item.route || "—"}</small></td><td>{item.ip_address || "—"}</td>
                    <td><small className="text-muted">{detailText(item.details)}</small></td>
                  </tr>
                ))}</tbody>
              </Table>
              {!data.activity.length && <Alert variant="info" className="mt-3">Todavía no hay actividad registrada.</Alert>}
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Cerrar</Button></Modal.Footer>
    </Modal>
  );
}
